import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Bell, Flame, Footprints, History, Loader2, Moon, Pause, Play, Radio, Share2, SkipForward, Sparkles, Square, Star, Volume2, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { bulletin, wholeStory } from "@/lib/storyRead";
import { useSpeaker } from "@/components/home/useSpeaker";

const ICONS = { radio: Radio, spark: Zap, bell: Bell, walk: Footprints, star: Star };
const AUTO_KEY = "cml_auto_morning";
const AUTO_DAY = "cml_auto_morning_day";
const LENGTH_KEY = "cml_morning_minutes";

// The ways MiLu can start someone's day, and the player that reads them out.
export const MorningMenu = ({ menu, lang, onUsed, hideGreeting = false, autoPick = "" }) => {
  const speaker = useSpeaker(menu.voice, menu.speed);
  const [busy, setBusy] = useState("");
  const [spot, setSpot] = useState(null);
  const [week, setWeek] = useState(null);
  const [weekOpen, setWeekOpen] = useState(false);
  const [streak, setStreak] = useState(menu.streak || 0);
  const [lastListen, setLastListen] = useState(null);
  const [sharing, setSharing] = useState(false);
  const [sleep, setSleep] = useState(() => Number(localStorage.getItem("cml_sleep_mins") || 0));
  const bedtime = useRef(null);
  const [walkNote, setWalkNote] = useState("");

  // a sleep timer: after so many minutes the listen fades away on its own
  const armSleep = (mins = sleep) => {
    clearTimeout(bedtime.current);
    if (mins > 0) {
      bedtime.current = setTimeout(() => speaker.fadeOut(8), Math.max(1, mins * 60 - 8) * 1000);
    }
  };

  const setSleepFor = (mins) => {
    setSleep(mins);
    localStorage.setItem("cml_sleep_mins", String(mins));
    if (speaker.playing || speaker.loading) armSleep(mins);
    else clearTimeout(bedtime.current);
  };

  const stopAll = () => {
    clearTimeout(bedtime.current);
    speaker.stop();
  };

  useEffect(() => () => clearTimeout(bedtime.current), []);

  const labelFor = (key) => menu.choices?.find((c) => c.key === key)?.label || key;

  // this morning's listen as a card a friend can open
  const shareMorning = async () => {
    setSharing(true);
    try {
      const { data } = await api.post("/news/morning/share", {
        label: lastListen.label, titles: lastListen.titles, lang,
      });
      if (navigator.share) {
        await navigator.share({ title: lastListen.label, url: data.link });
        return;
      }
      try {
        await navigator.clipboard.writeText(data.link);
        toast.success("Link copied — paste it anywhere");
      } catch {
        toast.success("Your card is ready", { description: data.link });
      }
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not make that card");
    } finally {
      setSharing(false);
    }
  };
  const started = useRef("");
  const [autoOn, setAutoOn] = useState(localStorage.getItem(AUTO_KEY) !== "0");
  const [mins, setMins] = useState(Number(localStorage.getItem(LENGTH_KEY) || 0));

  // where yesterday's — or this morning's — listen was interrupted
  useEffect(() => {
    api.get("/news/morning/spot")
      .then(({ data }) => data.stories?.length > 1 && data.at > 0 && setSpot(data))
      .catch(() => {});
  }, []);

  const lines = () =>
    api.get("/news/bulletin/lines", { params: { lang } }).then((r) => r.data).catch(() => null);

  const runQueue = (pick, stories, said, from = 0) => {
    const rows = stories.slice(from);
    setLastListen({ label: labelFor(pick), titles: rows.slice(0, 3).map((r) => r.title) });
    const items = bulletin(rows, lang, said).map((item, i) => ({
      ...item,
      getText: async () => {
        api.put("/news/morning/spot", { pick, stories, at: from + i }).catch(() => {});
        return item.getText();
      },
    }));
    setSpot(null);
    return speaker.playQueue(items, () => {
      api.delete("/news/morning/spot").catch(() => {});
    });
  };

  const resume = async () => {
    onUsed?.();
    armSleep();
    setBusy("resume");
    try {
      await runQueue(spot.pick || "news", spot.stories, await lines(), spot.at);
    } finally {
      setBusy("");
    }
  };

  // everything, in order: the news, the things they follow, their digest, then the walks
  const EVERYTHING = ["news", "preference", "digest", "walk5", "walk10", "walk20"];

  const readEverything = async () => {
    if (speaker.playing || speaker.loading) speaker.stop();
    onUsed?.();
    armSleep();
    api.post("/news/morning/listened")
      .then(({ data }) => setStreak(data.streak))
      .catch(() => {});
    setBusy("all");
    setWalkNote("");
    try {
      const said = await lines();
      const items = [];
      for (const key of EVERYTHING) {
        const choice = menu.choices?.find((c) => c.key === key);
        if (!choice) continue;
        if (key.startsWith("walk")) {
          const minutes = Number(key.slice(4));
          items.push({
            key: `all-${key}`, label: choice.label, voice: menu.walk_voice || menu.voice,
            getText: async () => {
              const { data } = await api.get("/news/walk",
                                             { params: { lang, minutes_wanted: minutes },
                                               timeout: 90000 });
              if (!data.text) throw new Error("Nothing short enough for that walk");
              return data.text;
            },
          });
          continue;
        }
        // eslint-disable-next-line no-await-in-loop
        const { data } = await api.get("/news/morning/stories", { params: { pick: key, lang } });
        if (data.stories?.length) items.push(...bulletin(data.stories, lang, said));
      }
      if (!items.length) {
        toast.info("Nothing waiting yet — follow a topic or two in News");
        return;
      }
      // a five, ten or twenty minute morning: he stops when the time is up
      const budget = Number(localStorage.getItem(LENGTH_KEY) || 0);
      const trimmed = [];
      if (budget) {
        let spent = 0;
        for (const item of items) {
          if (spent >= budget * 60) break;
          trimmed.push(item);
          spent += item.key?.startsWith("all-walk")
            ? Number(item.key.replace("all-walk", "")) * 60
            : 45;
        }
      }
      const queue = budget && trimmed.length ? trimmed : items;
      setLastListen({ label: budget ? `Your ${budget} minute morning` : "Everything this morning",
                      titles: queue.slice(0, 3).map((i) => i.label) });
      setSpot(null);
      await speaker.playQueue(queue, () => api.delete("/news/morning/spot").catch(() => {}));
    } catch (e) {
      toast.error(e.message || "MiLu could not read that out");
    } finally {
      setBusy("");
    }
  };

  // before nine, MiLu can simply start reading — no tap needed
  const auto = useRef(false);
  useEffect(() => {
    if (hideGreeting || auto.current) return;
    if (localStorage.getItem(AUTO_KEY) === "0") return;
    if (!menu.choices?.length || (menu.local_hour ?? 99) >= 9) return;
    if (localStorage.getItem(AUTO_DAY) === menu.day) return;
    auto.current = true;
    localStorage.setItem(AUTO_DAY, menu.day);
    readEverything().catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu.choices, menu.local_hour, menu.day, hideGreeting]);

  const pick = async (choice) => {
    if (speaker.playing || speaker.loading) speaker.stop();
    onUsed?.();
    armSleep();
    api.post("/news/morning/listened")
      .then(({ data }) => setStreak(data.streak))
      .catch(() => {});
    setBusy(choice.key);
    setWalkNote("");
    try {
      if (choice.key.startsWith("walk")) {
        const minutes = Number(choice.key.slice(4));
        await speaker.play(`morning-${choice.key}`, async () => {
          const { data } = await api.get("/news/walk", {
            params: { lang, minutes_wanted: minutes },
            timeout: 90000,
          });
          if (!data.text) throw new Error("Nothing short enough for a walk this morning");
          setWalkNote(data.distance || `${data.minutes} min`);
          return data.text;
        }, choice.label, menu.walk_voice || menu.voice);
        setLastListen({ label: choice.label, titles: [] });
        return;
      }
      const { data } = await api.get("/news/morning/stories", { params: { pick: choice.key, lang } });
      if (!data.stories?.length) {
        toast.info("Nothing waiting there yet — follow a topic or two in News");
        return;
      }
      await runQueue(choice.key, data.stories, await lines());
    } catch (e) {
      toast.error(e.message || "MiLu could not read that out");
    } finally {
      setBusy("");
    }
  };

  const reading = Boolean(speaker.playing || speaker.loading);
  const pill = "group flex items-center gap-2.5 rounded-full border border-white/10 bg-panel px-4 py-2.5 text-start text-sm font-normal text-ink transition-transform duration-200 hover:-translate-y-0.5 hover:border-brand/50 active:scale-[0.98]";

  // a choice tapped from the morning email opens straight into playback
  useEffect(() => {
    if (!autoPick || started.current === autoPick) return;
    const choice = menu.choices?.find((c) => c.key === autoPick);
    if (!choice) return;
    started.current = autoPick;
    pick(choice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoPick, menu.choices]);

  const openWeek = async () => {
    setWeekOpen((v) => !v);
    if (week) return;
    try {
      const { data } = await api.get("/news/listened", { params: { days: 7 } });
      setWeek(data.rows || []);
    } catch {
      setWeek([]);
    }
  };

  return (
    <div data-testid="morning-menu" className="space-y-2.5">
      {!hideGreeting && (
        <p data-testid="morning-greeting" className="text-sm leading-relaxed text-ink" dir="auto">
          {menu.greeting}
        </p>
      )}

      {streak > 0 && (
        <p
          data-testid="morning-streak"
          title={`${streak} morning${streak === 1 ? "" : "s"} in a row`}
          className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-2.5 py-1 text-[11px] text-amber-300"
        >
          <Flame className="h-3 w-3" />
          {streak} morning{streak === 1 ? "" : "s"} in a row
        </p>
      )}

      {lastListen && (
        <button
          data-testid="morning-share"
          onClick={shareMorning}
          disabled={sharing}
          className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-2.5 py-1 text-[11px] text-ink-soft transition-colors hover:border-brand/50 hover:text-brand disabled:opacity-60"
        >
          {sharing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Share2 className="h-3 w-3" />}
          Share my morning
        </button>
      )}

      <div className="flex flex-col gap-2">
        {menu.choices.length > 1 && (
          <>
            {!hideGreeting && (
              <div className="flex items-center gap-2">
                <p data-testid="morning-everything-ask" className="text-sm text-ink-soft">
                  Shall I read you everything this morning?
                </p>
                <button
                  data-testid="morning-auto-toggle"
                  onClick={() => {
                    const on = localStorage.getItem(AUTO_KEY) === "0";
                    localStorage.setItem(AUTO_KEY, on ? "1" : "0");
                    setAutoOn(on);
                    toast.success(on ? "I will start on my own before nine"
                                    : "I will wait for you to tap");
                  }}
                  className={`ms-auto shrink-0 rounded-full border px-2 py-0.5 text-[10px] transition-colors ${
                    autoOn ? "border-brand/60 bg-brand/10 text-brand" : "border-white/15 text-ink-soft"}`}
                >
                  {autoOn ? "starts on its own" : "waits for a tap"}
                </button>
              </div>
            )}
            <button
              data-testid="morning-everything"
              onClick={readEverything}
              dir="auto"
              className="group flex items-center gap-2.5 rounded-full border border-brand/60 bg-brand/15 px-4 py-2.5 text-start text-sm font-medium text-brand transition-transform duration-200 hover:-translate-y-0.5 active:scale-[0.98]"
            >
              {busy === "all"
                ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                : <Sparkles className="h-4 w-4 shrink-0" />}
              <span className="min-w-0 flex-1 truncate">
                {hideGreeting ? "Read me everything" : "Yes — read me everything"}
              </span>
              <span className="shrink-0 rounded-full bg-black/40 px-2 py-0.5 text-[11px] font-normal">
                {mins ? `${mins} min` : "news · yours · digest · walks"}
              </span>
            </button>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest text-ink-soft">
                How long
              </span>
              {[[5, "5 min"], [10, "10 min"], [20, "20 min"], [0, "the lot"]].map(([n, label]) => (
                <button
                  key={n}
                  data-testid={`morning-length-${n}`}
                  onClick={() => {
                    setMins(n);
                    localStorage.setItem(LENGTH_KEY, String(n));
                  }}
                  className={`rounded-full border px-2 py-0.5 text-[10px] transition-colors ${
                    mins === n ? "border-brand bg-brand/15 text-brand"
                               : "border-white/15 text-ink-soft hover:text-ink"}`}
                >
                  {label}
                </button>
              ))}
            </div>
          </>
        )}

        {spot && (
          <button data-testid="morning-resume" onClick={resume} dir="auto" className={pill}>
            {busy === "resume" ? (
              <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
            ) : (
              <History className="h-4 w-4 shrink-0 text-brand" />
            )}
            <span className="min-w-0 flex-1 truncate">
              {menu.resume_label || "Pick up where you left off"}
            </span>
            <span className="shrink-0 rounded-full border border-white/15 bg-black/60 px-2 py-0.5 text-[11px] font-normal text-brand">
              {spot.at + 1}/{spot.stories.length}
            </span>
          </button>
        )}

        {menu.choices.map((c) => {
          const Icon = ICONS[c.icon] || Radio;
          const loading = busy === c.key || speaker.loading === `morning-${c.key}`;
          return (
            <button
              key={c.key}
              data-testid={`morning-pick-${c.key}`}
              onClick={() => pick(c)}
              dir="auto"
              className={pill}
            >
              {loading ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
              ) : (
                <Icon className="h-4 w-4 shrink-0 text-brand" />
              )}
              <span className="min-w-0 flex-1 truncate">{c.label}</span>
              {c.badge && (
                <span
                  data-testid={`morning-badge-${c.key}`}
                  className="shrink-0 rounded-full border border-white/15 bg-black/60 px-2 py-0.5 text-[11px] font-normal text-brand"
                >
                  {c.badge}
                </span>
              )}
            </button>
          );
        })}

        <button data-testid="morning-week-toggle" onClick={openWeek} dir="auto" className={pill}>
          <History className="h-4 w-4 shrink-0 text-brand" />
          <span className="min-w-0 flex-1 truncate">
            {menu.week_label || "While You Slept"}
          </span>
          {week && (
            <span className="shrink-0 rounded-full border border-white/15 bg-black/60 px-2 py-0.5 text-[11px] font-normal text-brand">
              {week.length}
            </span>
          )}
        </button>

        <a data-testid="morning-bedtime" href="/bedtime" dir="auto" className={pill}>
          <Moon className="h-4 w-4 shrink-0 text-indigo-300" />
          <span className="min-w-0 flex-1 truncate">Before Bed</span>
        </a>

        {weekOpen && (
          <ol className="space-y-1 rounded-2xl border border-white/10 bg-black/40 px-3 py-2"
              data-testid="morning-week-list">
            {week === null && <li className="text-[11px] text-ink-soft">Looking it up…</li>}
            {week?.length === 0 && (
              <li className="text-[11px] text-ink-soft">Nothing read out yet this week.</li>
            )}
            {week?.map((row) => (
              <li key={row.url} className="flex items-start gap-2 text-xs"
                  data-testid={`morning-week-row-${row.story_id || row.url.slice(-8)}`}>
                <span className="min-w-0 flex-1" dir="auto">
                  {row.title}
                  {row.source && <span className="text-ink-soft"> · {row.source}</span>}
                </span>
                <button
                  data-testid={`morning-week-play-${row.story_id || row.url.slice(-8)}`}
                  onClick={() => speaker.play(`week-${row.url}`, wholeStory(row, lang), row.title)}
                  aria-label="Read it again"
                  className="shrink-0 text-ink-soft transition-colors hover:text-brand"
                >
                  <Volume2 className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>

      {reading && (
        <div
          data-testid="morning-player"
          className="flex items-center gap-2 rounded-2xl border border-brand/30 bg-brand/[0.07] px-3 py-2"
        >
          <p className="min-w-0 flex-1 truncate text-[11px] text-ink-soft" dir="auto">
            {speaker.said?.label || "Getting it…"}
            {walkNote && <span className="text-ink-soft"> · {walkNote}</span>}
            {speaker.queued.length > 0 && (
              <span className="text-brand"> · {speaker.queued.length} to come</span>
            )}
          </p>
          <select
            data-testid="morning-sleep"
            value={sleep}
            onChange={(e) => setSleepFor(Number(e.target.value))}
            title="Fade out after"
            aria-label="Fade out after"
            className="shrink-0 rounded-full border border-white/15 bg-black/40 px-1.5 py-0.5 text-[10px] text-ink-soft outline-none hover:border-brand/50"
          >
            <option value={0}>No timer</option>
            {[5, 10, 20, 30, 45].map((m) => (
              <option key={m} value={m}>{m} min</option>
            ))}
          </select>
          <button
            data-testid="morning-pause"
            onClick={speaker.togglePause}
            aria-label={speaker.paused ? "Carry on" : "Pause"}
            className="rounded-full border border-white/15 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
          >
            {speaker.paused ? <Play className="h-3 w-3" /> : <Pause className="h-3 w-3" />}
          </button>
          {speaker.queued.length > 0 && (
            <button
              data-testid="morning-skip"
              onClick={speaker.skip}
              aria-label="Next story"
              className="rounded-full border border-white/15 p-1.5 text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
            >
              <SkipForward className="h-3 w-3" />
            </button>
          )}
          <button
            data-testid="morning-stop"
            onClick={stopAll}
            aria-label="Stop"
            className="rounded-full border border-white/15 p-1.5 text-ink-soft transition-colors hover:border-destructive/60 hover:text-destructive"
          >
            <Square className="h-3 w-3" />
          </button>
        </div>
      )}
    </div>
  );
};
